/*
 * Copyright (C) Zoomdata, Inc. 2012-2022. All rights reserved.
 */
import { EmbeddedComponent } from './EmbeddedComponent';
import { ComponentTypes, EditorPlacements, SettingTypes, InteractivityProfile, } from './types';
export class EmbeddedVisualBuilder extends EmbeddedComponent {
    notificationSettings;
    breadcrumbs;
    header;
    source;
    visualId;
    route;
    componentType = ComponentTypes.VISUAL_BUILDER;
    interactivityOverrides;
    interactivityProfileName = InteractivityProfile.INTERACTIVE;
    menuEventsConfig;
    editor;
    visualLegendType;
    /**
     *
     * @param theme - The theme for the embedded dashboard. Valid values are `composer`, `modern`, or `dark`.
     * The initial default theme, `composer`, is the same as the `modern` theme.
     * However, if you add your own themes to the application, more options are available in this list and you may have introduced a different default.
     * @param application - `application.banner` Indicates whether the Composer top-level navigation banner should be shown with the embedded dashboard.
     * Valid values are `true` or `false`.
     *
     * `application.logo` Indicates whether the Composer logo should be shown with the embedded dashboard. Valid values are `true` or `false`.
     * @param renderer
     * @param visualId - The visual ID for an existing visual for visual authoring. If specified, the `source` property is ignored.
     * A valid visual ID should be specified in quotes (`"<visual-ID>"`).
     * If neither `visualId` or `source.visualId` is specified, an empty visual authoring instance will be opened.
     * If both are provided, `visualId` is used.
     * @param source - `source.visualId` The source ID of a visual template, used for creating a new visual in visual authoring.
     * This represents a predefined visual template. A valid source ID should be specified in quotes (`"<source-visual-ID>"`).
     * If neither `visualId` or `source.visualId` is specified, an empty visual authoring instance will be opened.
     * If both are provided, `visualId` is used.
     * @param header - `header.showActions` Indicates whether the visual builder header actions should be shown for the embedded visual builder. Valid values are `true` or `false`.
     *
     * `header.showTitle` Indicates whether the visual builder header title should be shown for the embedded visual builder. Valid values are `true` or `false`.
     *
     * `header.visible` Indicates whether the visual builder header should be visible for the embedded visual builder. Valid values are `true` or `false`.
     * @param breadcrumbs - `breadcrumbs.href` The link address of the breadcrumb title. A valid link address should be specified in quotes (`"<address>"`).
     *
     * `breadcrumbs.onClick` The click action handler for the breadcrumb title.
     *
     * `breadcrumbs.target` The link target parameter. Use `"_blank"` to open in a new tab. A valid link target should be specified in quotes (`"<target>"`).
     *
     * `breadcrumbs.title` The first item breadcrumbs title. A valid title should be specified in quotes (`"<title>"`).
     * @param interactivityOverrides - Specifies interactivity override settings for visual authoring.
     * The interactivity settings specified in this object will override any interactivity settings specified for the individual visuals.
     * For a list of visual interactivity settings you can specify
     * @param notificationSettings - Specifies settings for notifications
     * @param menuEventsConfig - Specifies event setup for context visual menu
     * @param editor - `editor.placement` Indicates where the visual editor appears. Valid editor placements are `dockRight` and `modals`.
     * @param visualLegendType - Specifies Visual Legend type for embedded Visual Builder
     * In case of `staticLegend` - composer legend will be used.
     * In case of `dynamicLegend` - Echarts legend will be used.
     * In other cases - legend type will be taken from feature toggle value
     */
    constructor(theme, application, renderer, visualId, source, header, breadcrumbs, interactivityOverrides, notificationSettings, menuEventsConfig, editor, visualLegendType) {
        super(theme, application, renderer, notificationSettings);
        this.notificationSettings = notificationSettings;
        this.visualId = visualId;
        this.source = source;
        this.breadcrumbs = {
            ...breadcrumbs,
        };
        this.header = {
            visible: true,
            showActions: true,
            showTitle: true,
            ...header,
        };
        this.editor = editor || {
            placement: EditorPlacements.DOCK_RIGHT,
        };
        this.interactivityOverrides = interactivityOverrides;
        this.menuEventsConfig = menuEventsConfig;
        this.visualLegendType = visualLegendType;
        if (visualId === null && source.visualId) {
            this.route = `visualization/visual/${this.componentInstanceId}?visualId=${source.visualId}`;
        }
        else if (visualId === null && source.sourceId) {
            const staticSource = source.staticSource ? `&staticSource=${source.staticSource}` : '';
            const visTypeId = source.visTypeId ? `&visId=${source.visTypeId}` : '';
            this.route = `visualization/visual/${this.componentInstanceId}?sourceId=${source.sourceId}${staticSource}${visTypeId}`;
        }
        else if (visualId === null) {
            this.route = `visualization/visual/${this.componentInstanceId}`;
        }
        else {
            this.route = `visualization/visual-${visualId}/${this.componentInstanceId}`;
        }
    }
    getApplicationParams() {
        const base = super.getApplicationParams();
        return {
            ...base,
            interactivityProfileName: this.interactivityProfileName,
            editor: this.editor,
            mode: 'interactive',
            breadcrumbs: this.breadcrumbs,
            header: this.header,
            route: this.route,
            interactivityOverrides: this.interactivityOverrides,
            menuEventsConfig: this.menuEventsConfig,
            visualLegendType: this.visualLegendType,
        };
    }
    render(element, attributes) {
        super.render(element, attributes);
        this.htmlElement.addEventListener('composer-visual-builder-loaded', this.removeLoader);
    }
    destroy() {
        this.htmlElement.removeEventListener('composer-visual-builder-loaded', this.removeLoader);
    }
    addEventListener(eventName, callback) {
        this.htmlElement.addEventListener(eventName, callback);
    }
    removeEventListener(eventName, callback) {
        this.htmlElement.removeEventListener(eventName, callback);
    }
}
//# sourceMappingURL=EmbeddedVisualBuilder.js.map